import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Heart, Mail, Lock, Phone, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // Reset method: 'email' or 'phone'
  const [resetMethod, setResetMethod] = useState('email');
  
  const [step, setStep] = useState(token ? 'reset' : 'request');
  const [loading, setLoading] = useState(false);
  
  // Email reset
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState(token || '');
  
  // Phone reset
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  
  // New password
  const [newPassword, setNewPassword] = useState('');

  // Request reset via Email
  const handleRequestEmailReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/request-reset`, { email });
      toast.success('Link de resetare trimis pe email!');
      
      // For demo purposes, show token
      if (response.data.demo_token) {
        setResetToken(response.data.demo_token);
        setStep('reset');
        toast.info(`[Demo] Token: ${response.data.demo_token}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error('Eroare la trimiterea linkului');
    } finally {
      setLoading(false);
    }
  };

  // Request reset via Phone (send OTP)
  const handleRequestPhoneReset = async () => {
    if (!phone) {
      toast.error('Introdu numărul de telefon');
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/phone/request-reset`, { phone });
      toast.success('Cod OTP trimis pe telefon!');
      setOtpSent(true);
      
      // For demo purposes, show OTP
      if (response.data.demo_otp) {
        toast.info(`[Demo] Cod OTP: ${response.data.demo_otp}`, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la trimiterea codului OTP');
    } finally {
      setLoading(false);
    }
  };

  // Reset password with Email token
  const handleResetPasswordEmail = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword,
      });
      toast.success('Parola a fost resetată cu succes!');
      setTimeout(() => navigate('/auth'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Token invalid sau expirat');
    } finally {
      setLoading(false);
    }
  };

  // Reset password with Phone OTP
  const handleResetPasswordPhone = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/phone/reset-password`, {
        phone,
        otp: otpCode,
        new_password: newPassword,
      });
      toast.success('Parola a fost resetată cu succes!');
      setTimeout(() => navigate('/auth'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cod OTP invalid sau expirat');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center px-6 py-12">
      <div
        
        
        
        className="w-full max-w-md"
      >
        <div className="flex items-center justify-center space-x-2 mb-8">
          <div className="w-12 h-12 bg-[#1A4D2E] rounded-full flex items-center justify-center">
            <Heart className="w-7 h-7 text-white" />
          </div>
          <span className="text-3xl font-serif font-bold text-[#1A4D2E]">NutriHealth</span>
        </div>

        <Card className="border-none shadow-xl">
          <CardHeader>
            <CardTitle className="font-serif text-2xl text-center text-[#1A4D2E]">
              Resetare Parolă
            </CardTitle>
            <CardDescription className="text-center">
              {step === 'request' 
                ? 'Alege metoda de resetare a parolei'
                : 'Creează o parolă nouă'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'request' ? (
              <>
                {/* Method Toggle */}
                <div className="flex justify-center mb-6 space-x-2">
                  <Button
                    type="button"
                    variant={resetMethod === 'email' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setResetMethod('email'); setOtpSent(false); }}
                    className={resetMethod === 'email' 
                      ? 'bg-[#1A4D2E] text-white' 
                      : 'border-[#1A4D2E]/30 text-[#1A4D2E]'}
                  >
                    <Mail className="w-4 h-4 mr-1" />
                    Email
                  </Button>
                  <Button
                    type="button"
                    variant={resetMethod === 'phone' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setResetMethod('phone'); setOtpSent(false); }}
                    className={resetMethod === 'phone' 
                      ? 'bg-[#1A4D2E] text-white' 
                      : 'border-[#1A4D2E]/30 text-[#1A4D2E]'}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    SMS
                  </Button>
                </div>

                {resetMethod === 'email' ? (
                  // Email Reset Request
                  <form onSubmit={handleRequestEmailReset} className="space-y-4" data-testid="reset-request-form">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center space-x-2">
                        <Mail className="w-4 h-4" />
                        <span>Email</span>
                      </Label>
                      <Input
                        id="email"
                        data-testid="reset-email-input"
                        type="email"
                        placeholder="adresa@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="send-reset-link-button"
                      className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                    >
                      {loading ? 'Se trimite...' : 'Trimite Link Resetare'}
                    </Button>
                  </form>
                ) : (
                  // Phone Reset Request
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center space-x-2">
                        <Phone className="w-4 h-4" />
                        <span>Număr de telefon</span>
                      </Label>
                      <Input
                        id="phone"
                        data-testid="reset-phone-input"
                        type="tel"
                        placeholder="+40 712 345 678"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                      />
                    </div>

                    {!otpSent ? (
                      <Button
                        type="button"
                        onClick={handleRequestPhoneReset}
                        disabled={loading}
                        className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {loading ? 'Se trimite...' : 'Trimite Cod SMS'}
                      </Button>
                    ) : (
                      // OTP + New Password Form
                      <form onSubmit={handleResetPasswordPhone} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="otp" className="flex items-center space-x-2">
                            <MessageSquare className="w-4 h-4" />
                            <span>Cod OTP din SMS</span>
                          </Label>
                          <Input
                            id="otp"
                            data-testid="reset-otp-input"
                            type="text"
                            placeholder="123456"
                            maxLength={6}
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                            required
                            className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12 text-center text-xl tracking-widest"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="new-password-phone" className="flex items-center space-x-2">
                            <Lock className="w-4 h-4" />
                            <span>Parolă Nouă</span>
                          </Label>
                          <Input
                            id="new-password-phone"
                            data-testid="new-password-phone-input"
                            type="password"
                            placeholder="Parolă nouă (min. 8 caractere)"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={8}
                            className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                          />
                        </div>
                        
                        <Button
                          type="submit"
                          disabled={loading || otpCode.length !== 6}
                          data-testid="reset-password-phone-button"
                          className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                        >
                          {loading ? 'Se resetează...' : 'Resetează Parola'}
                        </Button>
                        
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => { setOtpSent(false); setOtpCode(''); setNewPassword(''); }}
                          className="w-full text-[#4a5568]"
                        >
                          ← Trimite alt cod
                        </Button>
                      </form>
                    )}
                  </div>
                )}
              </>
            ) : (
              // Email Token Reset Form
              <form onSubmit={handleResetPasswordEmail} className="space-y-4" data-testid="reset-password-form">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-2">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Folosește tokenul primit pe email pentru a reseta parola.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="token">Token Resetare</Label>
                  <Input
                    id="token"
                    data-testid="reset-token-input"
                    type="text"
                    placeholder="Token din email"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    required
                    className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="flex items-center space-x-2">
                    <Lock className="w-4 h-4" />
                    <span>Parolă Nouă</span>
                  </Label>
                  <Input
                    id="new-password"
                    data-testid="new-password-input"
                    type="password"
                    placeholder="Parolă nouă (min. 8 caractere)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] rounded-lg h-12"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-password-button"
                  className="w-full bg-[#1A4D2E] text-white rounded-full hover:bg-[#143d24] h-12"
                >
                  {loading ? 'Se resetează...' : 'Resetează Parola'}
                </Button>
              </form>
            )}
            
            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/auth')}
                className="text-sm text-[#1A4D2E] hover:underline"
              >
                Înapoi la Autentificare
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
